import { useCallback, useEffect, useRef, useState } from 'react';
import { getViewerDisplayPref, patchViewerDisplay } from './viewer-display-pref';

/**
 * Minuterie de diaporama (XPHOTO-21) : avance automatique à intervalle réglable,
 * pause, boucle. Logique isolée (testable avec des minuteurs simulés).
 *
 * - `onAdvance` est appelé à chaque tick (le consommateur décide quoi faire :
 *   passer au suivant, ou s'arrêter en fin sans boucle). Stocké dans une ref : sa
 *   ré-identité NE réinitialise PAS la minuterie.
 * - `resetKey` (typiquement l'index courant) réinitialise la cadence à chaque
 *   changement → un pas manuel relance un intervalle plein.
 * - `media` (optionnel) décrit le média COURANT et permet de le laisser aller jusqu'au
 *   bout au lieu de le couper à 5 s. La POLITIQUE vit ici (`holdSecondsFor`, pure et
 *   testée) et non chez l'appelant : elle a besoin du plafond et de la cadence, qui
 *   sont l'état de ce hook — la calculer dehors créerait une dépendance circulaire.
 * - `loop` et `speedIndex` sont INITIALISÉS depuis la préférence d'affichage persistée
 *   (`viewer-display-pref`) et RÉÉCRITS à chaque changement. `playing` reste éphémère.
 */

/** Vitesses proposées, en secondes par média. */
export const SLIDESHOW_SPEEDS = [3, 5, 10] as const;

/**
 * Plafonds proposés pour un média qu'on laisse aller jusqu'au bout (vidéo, image
 * animée), en secondes. `0` = SANS LIMITE : on attend la fin réelle, quelle qu'elle
 * soit. Un média statique n'est jamais concerné — il garde la cadence.
 */
export const SLIDESHOW_CAPS = [15, 30, 60, 0] as const;

/**
 * Le média courant, du point de vue de la minuterie.
 *
 * - `awaitsEnd` : le média SIGNALE lui-même sa fin (un `<video>` émet `ended`). La
 *   minuterie n'est alors qu'un PLAFOND de sécurité — c'est l'événement qui avance.
 * - `durationS` : durée connue du média. Pour une image ANIMÉE, c'est la durée d'un
 *   cycle mesurée au scan ; une image statique n'en a pas (`null`).
 */
export interface SlideshowMedia {
  awaitsEnd: boolean;
  durationS: number | null;
}

/**
 * Durée d'affichage du média courant, en secondes — `Infinity` = aucune minuterie.
 * PURE et exportée pour être testée directement.
 *
 * - Média qui signale sa fin (vidéo) : on n'arme que le plafond ; sans plafond, aucune
 *   minuterie du tout (`Infinity`) et l'avance vient exclusivement de `ended`.
 * - Média de durée connue (image animée) : on laisse passer UN CYCLE complet, avec un
 *   PLANCHER à la cadence — un GIF de 0,4 s qui défilerait en 0,4 s serait illisible —
 *   et le plafond en borne supérieure.
 * - Tout le reste (image statique, durée inconnue) : la cadence.
 */
export function holdSecondsFor(
  media: SlideshowMedia | null | undefined,
  speedSeconds: number,
  capSeconds: number,
): number {
  if (!media) return speedSeconds;
  if (media.awaitsEnd) return capSeconds > 0 ? capSeconds : Infinity;
  const cycle = media.durationS;
  if (cycle === null || !Number.isFinite(cycle) || cycle <= 0) return speedSeconds;
  const floored = Math.max(cycle, speedSeconds);
  return capSeconds > 0 ? Math.min(floored, capSeconds) : floored;
}

export interface UseSlideshowResult {
  playing: boolean;
  setPlaying: (value: boolean) => void;
  toggle: () => void;
  loop: boolean;
  toggleLoop: () => void;
  speedSeconds: number;
  /** Passe à la vitesse suivante (cycle 3 → 5 → 10 → 3…). */
  cycleSpeed: () => void;
  /** Plafond d'attente d'un média long, en secondes (`0` = sans limite). */
  capSeconds: number;
  /** Passe au plafond suivant (cycle 15 → 30 → 60 → sans limite → 15…). */
  cycleCap: () => void;
}

export function useSlideshow(
  onAdvance: () => void,
  resetKey: unknown,
  media?: SlideshowMedia | null,
): UseSlideshowResult {
  const [playing, setPlaying] = useState(false);
  // Boucle & vitesse : reprises de la préférence persistée (défauts de fabrique sinon).
  const [loop, setLoop] = useState(() => getViewerDisplayPref().slideshowLoop);
  const [speedIndex, setSpeedIndex] = useState(() => getViewerDisplayPref().slideshowSpeedIndex);
  const [capIndex, setCapIndex] = useState(() => getViewerDisplayPref().slideshowCapIndex);
  // `speedIndex` provient toujours d'un index BORNÉ (préférence ou `cycleSpeed` modulo) ;
  // le `??` reste un garde-fou de typage (index tuple → `number | undefined`).
  const speedSeconds = SLIDESHOW_SPEEDS[speedIndex] ?? SLIDESHOW_SPEEDS[1];
  const capSeconds = SLIDESHOW_CAPS[capIndex] ?? SLIDESHOW_CAPS[2];

  // Persiste boucle + vitesse à chaque changement (jamais `playing`, éphémère).
  useEffect(() => {
    patchViewerDisplay({
      slideshowLoop: loop,
      slideshowSpeedIndex: speedIndex,
      slideshowCapIndex: capIndex,
    });
  }, [loop, speedIndex, capIndex]);

  const cbRef = useRef(onAdvance);
  useEffect(() => {
    cbRef.current = onAdvance;
  }, [onAdvance]);

  const holdEffective = holdSecondsFor(media, speedSeconds, capSeconds);

  useEffect(() => {
    if (!playing) return;
    // Plafond « sans limite » sur une vidéo : aucune minuterie n'est armée, l'avance
    // vient exclusivement de l'événement `ended`. Sans ce garde-fou, `setInterval`
    // recevrait `Infinity` — que la spec convertit en 0, soit une avance en boucle
    // serrée qui ferait défiler toute la bibliothèque en quelques secondes.
    if (!Number.isFinite(holdEffective)) return;
    const id = setInterval(() => cbRef.current(), holdEffective * 1000);
    return () => clearInterval(id);
    // `resetKey` (index) relance la cadence à chaque avance / pas manuel.
  }, [playing, holdEffective, resetKey]);

  const toggle = useCallback(() => setPlaying((p) => !p), []);
  const toggleLoop = useCallback(() => setLoop((l) => !l), []);
  const cycleSpeed = useCallback(() => setSpeedIndex((i) => (i + 1) % SLIDESHOW_SPEEDS.length), []);
  const cycleCap = useCallback(() => setCapIndex((i) => (i + 1) % SLIDESHOW_CAPS.length), []);

  return {
    playing,
    setPlaying,
    toggle,
    loop,
    toggleLoop,
    speedSeconds,
    cycleSpeed,
    capSeconds,
    cycleCap,
  };
}
